import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { StreamPalette, PoolPalette } from './palettes';
import { fonts, monoStyle, ease, duration } from './palettes';
import { fmtMoney } from '../data/ledger';

type AnyPalette = StreamPalette | PoolPalette;

interface PaletteProps<P> {
  c: P;
}

// Below this size the goo filter visibly blurs into a meatball — switch to
// the static three-circle fallback per the brand sheet rule.
const GOO_FILTER_MIN_PX = 14;

type MarkAnimation = 'idle' | 'reveal' | 'loading';

interface MarkProps {
  size?: number;
  color?: string;
  animated?: MarkAnimation | null;
  className?: string;
}

// The Pool Dish mark — a puddle with three beads dropping into it.
// `color` overrides the default lagoon brand color.
export function GFLogo({ c, size = 24, color, animated, className }: PaletteProps<AnyPalette> & MarkProps) {
  return <PoolDishMark size={size} color={color ?? c.accent} animated={animated} className={className} />;
}

// PoolLogo predates the brand consolidation. The brand sheet specifies a
// single mark for the whole product, so this just calls through to the same
// pool-dish renderer. Kept as a named export so callers don't need editing.
export function PoolLogo({ c, size = 32, color, animated, className }: PaletteProps<PoolPalette> & MarkProps) {
  return <PoolDishMark size={size} color={color ?? c.accent} animated={animated} className={className} />;
}

// Brand-aligned loading indicator. Replaces the bare CSS spinner so loading
// states feel like the rest of the product. Pairs with `gf-loading` keyframes
// from `GooFilter`.
export function BrandSpinner({ size = 56, color = '#0A6E7E' }: { size?: number; color?: string }) {
  return (
    <span className="gf-loading" role="status" aria-label="Loading">
      <PoolDishMark size={size} color={color} animated="loading" />
    </span>
  );
}

// Full-viewport branded loading screen. Used by ProtectedRoute (auth bootstrap)
// and SchedulerPage (data fetch). Pulled out as a primitive so future routes
// that need a "centered spinner with optional caption" all look the same.
export function LoadingScreen({
  caption,
  size = 72,
  background = '#F6F0E0',
  color = '#0A6E7E',
}: {
  caption?: string;
  size?: number;
  background?: string;
  color?: string;
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        background,
        color,
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      <BrandSpinner size={size} color={color} />
      {caption !== undefined && (
        <div style={{ fontSize: 13.5, color, opacity: 0.7 }}>{caption}</div>
      )}
    </div>
  );
}

export function PoolDishMark({ size = 24, color = '#0A6E7E', animated = null, className }: MarkProps) {
  const useFilter = size >= GOO_FILTER_MIN_PX;
  const motionClass = animated ? `gf-${animated}` : undefined;
  const svgClass = [motionClass, className].filter(Boolean).join(' ') || undefined;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden
      className={svgClass}
      style={{ color, display: 'inline-block' }}
    >
      <g filter={useFilter ? 'url(#goo)' : undefined} fill="currentColor">
        <ellipse className={animated ? 'gf-dish' : undefined} cx="50" cy="62" rx="32" ry="18" />
        <circle className={animated ? 'gf-bead-1' : undefined} cx="34" cy="44" r="8" />
        <circle className={animated ? 'gf-bead-2' : undefined} cx="50" cy="38" r="10" />
        <circle className={animated ? 'gf-bead-3' : undefined} cx="68" cy="46" r="7" />
      </g>
    </svg>
  );
}

// SVG <defs> block — once per app. Loads the brand `goo` filter and the keyframes
// for the two scripted mark states (idle breathe, coalesce reveal). prefers-reduced-motion
// suppresses the animations.
export function GooFilter() {
  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden>
        <defs>
          {/* A wider blur (2.8) fuses the beads into the dish so the silhouette
              reads as one smooth blob, and a gentler alpha re-sharpen (slope 9,
              offset -4 — vs the old 16/-8) keeps the threshold at ~0.5 alpha but
              widens the anti-aliased edge band, killing the choppy scalloped
              outline the steep curve produced at small render sizes. */}
          <filter id="goo" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2.8" />
            <feColorMatrix values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 9 -4" />
          </filter>
        </defs>
      </svg>
      <style>{`
        @keyframes gf-idle-pulse { 0%,100% { transform: scaleX(1) scaleY(1); } 50% { transform: scaleX(1.02) scaleY(.97); } }
        @keyframes gf-bead-bob   { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-2px); } }
        @keyframes gf-coalesce   { 0% { transform: scale(0); opacity: 0; } 40% { transform: scale(.4); opacity: .6; } 100% { transform: scale(1); opacity: 1; } }
        @keyframes gf-fly-1 { 0%,60% { transform: translate(-40px,-30px) scale(0); opacity:0; } 80% { transform: translate(0,0) scale(1.2); opacity:1; } 100% { transform: translate(0,0) scale(1); } }
        @keyframes gf-fly-2 { 0%,65% { transform: translate(40px,-40px)  scale(0); opacity:0; } 85% { transform: translate(0,0) scale(1.2); opacity:1; } 100% { transform: translate(0,0) scale(1); } }
        @keyframes gf-fly-3 { 0%,70% { transform: translate(0,-50px)      scale(0); opacity:0; } 90% { transform: translate(0,0) scale(1.2); opacity:1; } 100% { transform: translate(0,0) scale(1); } }
        @keyframes gf-flash-1 { 0%,40%,100% { opacity:.3 } 10%,20% { opacity:1 } }
        @keyframes gf-flash-2 { 0%,20%,60%,100% { opacity:.3 } 30%,40% { opacity:1 } }
        @keyframes gf-flash-3 { 0%,40%,80%,100% { opacity:.3 } 50%,60% { opacity:1 } }
        .gf-idle .gf-dish   { animation: gf-idle-pulse ${duration.idle} ${ease.pulse} infinite; transform-origin: 50px 62px; transform-box: fill-box; }
        .gf-idle .gf-bead-1 { animation: gf-bead-bob   ${duration.idle} ${ease.pulse} infinite -0.2s; transform-origin: 34px 44px; transform-box: fill-box; }
        .gf-idle .gf-bead-2 { animation: gf-bead-bob   ${duration.idle} ${ease.pulse} infinite -0.5s; transform-origin: 50px 38px; transform-box: fill-box; }
        .gf-idle .gf-bead-3 { animation: gf-bead-bob   ${duration.idle} ${ease.pulse} infinite -0.8s; transform-origin: 68px 46px; transform-box: fill-box; }
        .gf-reveal .gf-dish   { animation: gf-coalesce ${duration.reveal} ${ease.gel} both;     transform-origin: 50px 62px; transform-box: fill-box; }
        .gf-reveal .gf-bead-1 { animation: gf-fly-1   ${duration.reveal} ${ease.gel} both;     transform-origin: 34px 44px; transform-box: fill-box; }
        .gf-reveal .gf-bead-2 { animation: gf-fly-2   ${duration.reveal} ${ease.gel} both;     transform-origin: 50px 38px; transform-box: fill-box; }
        .gf-reveal .gf-bead-3 { animation: gf-fly-3   ${duration.reveal} ${ease.gel} both;     transform-origin: 68px 46px; transform-box: fill-box; }
        .gf-loading .gf-bead-1 { animation: gf-flash-1 ${duration.loading} linear infinite; }
        .gf-loading .gf-bead-2 { animation: gf-flash-2 ${duration.loading} linear infinite; }
        .gf-loading .gf-bead-3 { animation: gf-flash-3 ${duration.loading} linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .gf-idle .gf-dish, .gf-idle .gf-bead-1, .gf-idle .gf-bead-2, .gf-idle .gf-bead-3,
          .gf-reveal .gf-dish, .gf-reveal .gf-bead-1, .gf-reveal .gf-bead-2, .gf-reveal .gf-bead-3,
          .gf-loading .gf-bead-1, .gf-loading .gf-bead-2, .gf-loading .gf-bead-3 {
            animation: none;
          }
        }
      `}</style>
    </>
  );
}

// "Gooey." in Instrument Serif. The period is set in Lagoon — it's the
// brand's signature glyph and shouldn't be recolored.
export function Wordmark({
  c,
  size = 28,
  withPeriod = true,
  style,
}: PaletteProps<AnyPalette> & { size?: number; withPeriod?: boolean; style?: CSSProperties }) {
  return (
    <span
      style={{
        fontFamily: fonts.brand,
        fontWeight: 400,
        fontSize: size,
        letterSpacing: -size * 0.025,
        lineHeight: 1,
        color: c.ink,
        ...style,
      }}
    >
      Gooey{withPeriod && <span style={{ color: c.accent }}>.</span>}
    </span>
  );
}

// Mark + wordmark + optional italic tagline. The standard sign-in / app-launch lockup.
export function Lockup({
  c,
  size = 28,
  tagline = 'Money, on schedule.',
  animated = null,
  align = 'row',
  to,
}: PaletteProps<AnyPalette> & {
  size?: number;
  tagline?: string | null;
  animated?: 'idle' | 'reveal' | null;
  align?: 'row' | 'column';
  /** When set, the lockup becomes a router link to this path (e.g. "/" for home). */
  to?: string;
}) {
  const markSize = Math.round(size * 1.6);
  const inner =
    align === 'column' ? (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <PoolDishMark size={markSize} color={c.accent} animated={animated} className={animated ? `gf-${animated}` : undefined} />
        <Wordmark c={c} size={size} />
        {tagline && (
          <span style={{ fontFamily: fonts.brand, fontStyle: 'italic', fontSize: size * 0.72, color: c.accent, letterSpacing: -0.4, lineHeight: 1 }}>
            {tagline}
          </span>
        )}
      </div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <PoolDishMark size={markSize} color={c.accent} animated={animated} className={animated ? `gf-${animated}` : undefined} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Wordmark c={c} size={size} />
          {tagline && (
            <span style={{ fontFamily: fonts.brand, fontStyle: 'italic', fontSize: size * 0.6, color: c.accent, letterSpacing: -0.2, lineHeight: 1 }}>
              {tagline}
            </span>
          )}
        </div>
      </div>
    );

  if (to) {
    return (
      <Link
        to={to}
        aria-label="Gooey home"
        style={{ color: 'inherit', textDecoration: 'none', display: 'inline-block', width: 'fit-content' }}
      >
        {inner}
      </Link>
    );
  }
  return inner;
}

export type ViewMode = 'list' | 'calendar';

interface ViewToggleProps {
  c: AnyPalette;
  value: ViewMode;
  onChange: (v: ViewMode) => void;
  variant?: 'stream' | 'pool';
}

export function ViewToggle({ c, value, onChange, variant = 'stream' }: ViewToggleProps) {
  const isList = value === 'list';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 2, padding: 3,
        background: c.bg, borderRadius: 999, border: `1px solid ${c.line}`,
      }}
    >
      <ViewSeg c={c} active={isList} onClick={() => onChange('list')} icon="list" variant={variant}>
        List
      </ViewSeg>
      <ViewSeg c={c} active={!isList} onClick={() => onChange('calendar')} icon="cal" variant={variant}>
        Calendar
      </ViewSeg>
    </div>
  );
}

function ViewSeg({
  c, active, onClick, icon, children, variant,
}: {
  c: AnyPalette; active: boolean; onClick: () => void; icon: 'list' | 'cal';
  children: ReactNode; variant: 'stream' | 'pool';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 999,
        background: active ? c.surface : 'transparent',
        color: active ? c.ink : c.mute, fontSize: 12.5, fontWeight: active ? 600 : 500,
        cursor: 'pointer', border: 0, fontFamily: 'inherit',
        boxShadow: active ? (variant === 'pool' ? '0 2px 6px rgba(0,0,0,0.08)' : '0 1px 3px rgba(0,0,0,0.06)') : 'none',
      }}
    >
      {icon === 'list' ? (
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M2 3h8M2 6h8M2 9h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      ) : (
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden>
          <rect x="1.5" y="2.5" width="9" height="8" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
          <path d="M1.5 5h9M4 1.2v2M8 1.2v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
      )}
      {/* Wrapped so a host (e.g. the mobile scheduler header) can collapse the
          toggle to icons by hiding `.gf-seg-label`, without losing the icon. */}
      <span className="gf-seg-label">{children}</span>
    </button>
  );
}

interface FieldProps {
  c: StreamPalette;
  label?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (v: string) => void;
  type?: string;
  suffix?: string;
  required?: boolean;
  autoComplete?: string;
  // Hints for mobile keyboards / autofill. `inputMode="email"` is what gets
  // phones to surface the email keyboard (with the @ key); the rest keep the
  // OS from auto-capitalizing / auto-correcting an address.
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  autoCapitalize?: string;
  autoCorrect?: string;
  spellCheck?: boolean;
  // Inline error message. Truthy = render the message under the input AND
  // outline the input in the warning color. Falsy / empty string = no error
  // state (so callers can do `error={emailError || ''}` without conditionals).
  error?: string | null;
}

export function Field({
  c,
  label,
  placeholder,
  value,
  defaultValue,
  onChange,
  type,
  suffix,
  required,
  autoComplete,
  inputMode,
  autoCapitalize,
  autoCorrect,
  spellCheck,
  error,
}: FieldProps) {
  const hasError = typeof error === 'string' && error.length > 0;
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <span style={{ fontSize: 11.5, color: c.mute, letterSpacing: 0.6, textTransform: 'uppercase' }}>{label}</span>
      )}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: c.surface,
          border: `1px solid ${hasError ? c.warn : c.line}`,
          borderRadius: 10,
          padding: '12px 14px',
          boxShadow: hasError ? `0 0 0 3px ${c.warnBg}` : 'none',
        }}
      >
        <input
          type={type ?? 'text'}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          required={required}
          autoComplete={autoComplete}
          inputMode={inputMode}
          autoCapitalize={autoCapitalize}
          autoCorrect={autoCorrect}
          spellCheck={spellCheck}
          aria-invalid={hasError ? true : undefined}
          style={{
            flex: 1, border: 0, outline: 'none', background: 'transparent',
            fontSize: 14, fontFamily: 'inherit', color: c.ink, minWidth: 0,
          }}
        />
        {suffix && <span style={{ ...monoStyle, fontSize: 12, color: c.mute }}>{suffix}</span>}
      </div>
      {hasError && (
        <span role="alert" style={{ fontSize: 12.5, color: c.warn, marginTop: 2 }}>
          {error}
        </span>
      )}
    </label>
  );
}

// Form-level error banner. For API errors and cross-field issues that don't
// belong on one input. Returns null when there's nothing to say so callers
// can `<FormError c={c}>{apiError}</FormError>` unconditionally.
export function FormError({
  c,
  children,
}: {
  c: StreamPalette;
  children?: React.ReactNode;
}) {
  if (children === undefined || children === null || children === false || children === '') {
    return null;
  }
  return (
    <div
      role="alert"
      style={{
        background: c.warnBg,
        color: c.warn,
        borderRadius: 10,
        padding: '10px 12px',
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      {children}
    </div>
  );
}

// A small "ⓘ" info button that toggles a compact overlay with `text`.
// Closes on outside-click or Escape. Used for inline help that would
// otherwise clutter a form with always-on body copy.
export function InfoHint({ c, text, label = 'More info' }: { c: StreamPalette; text: string; label?: string }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 22, height: 22, borderRadius: '50%', padding: 0,
          border: `1.5px solid ${c.accent}`, background: c.surface,
          color: c.accent, cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 700, lineHeight: 1,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        i
      </button>
      {open && (
        <span
          role="tooltip"
          style={{
            // Anchor the tooltip's right edge to the trigger (which sits at the
            // right of its row) so it expands leftward and never clips off the
            // right side of a narrow modal on mobile.
            position: 'absolute', bottom: 'calc(100% + 8px)', right: 0,
            width: 220, maxWidth: '78vw', zIndex: 20,
            background: c.surface, color: c.ink, border: `1px solid ${c.line}`,
            borderRadius: 10, padding: '9px 11px', fontSize: 11.5, fontWeight: 400, lineHeight: 1.5,
            boxShadow: '0 8px 24px rgba(0,0,0,0.14)', textAlign: 'left',
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
}

export function Checkbox({ c, checked }: { c: StreamPalette; checked: boolean }) {
  return (
    <span
      style={{
        width: 16, height: 16, borderRadius: 4,
        border: `1.5px solid ${checked ? c.ink : c.mute}`,
        background: checked ? c.ink : 'transparent',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 9 9" aria-hidden style={{ color: c.surface }}>
          <path d="M1.5 4.5L3.5 6.5L7.5 2" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

export function Chip({
  c, active, children, onClick,
}: { c: StreamPalette; active?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '7px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 500,
        background: active ? c.ink : c.surface, color: active ? c.surface : c.ink,
        border: `1px solid ${active ? c.ink : c.line}`, cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

export function Pill({
  c, active, children, onClick,
}: { c: StreamPalette; active?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <span
      onClick={onClick}
      style={{
        padding: '5px 10px', borderRadius: 999, fontSize: 12,
        background: active ? c.ink : c.bg, color: active ? c.surface : c.ink,
        border: `1px solid ${active ? c.ink : c.line}`, cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </span>
  );
}

export function DowChip({
  c, active, children, onClick,
}: { c: StreamPalette; active?: boolean; children: ReactNode; onClick?: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 26, height: 26, borderRadius: '50%', padding: 0,
        background: active ? c.ink : c.bg, color: active ? c.surface : c.ink,
        border: `1px solid ${active ? c.ink : c.line}`, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 500,
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

export function Divider({ c, label }: { c: StreamPalette; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
      <div style={{ flex: 1, height: 1, background: c.line }} />
      <div style={{ fontSize: 11.5, color: c.mute, letterSpacing: 0.8, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ flex: 1, height: 1, background: c.line }} />
    </div>
  );
}

export function CategoryDot({ color, fallback }: { color?: string; fallback: string }) {
  return (
    <span
      style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: color ?? fallback,
      }}
    />
  );
}

// Shrink the balance font as the formatted string grows so a large amount
// (e.g. "$26,257,897.44") doesn't force the "Current balance" label to wrap on
// narrow screens. Returns a multiplier applied on top of the breakpoint's base
// size via the `--gf-money-scale` CSS variable.
function moneyFontScale(text: string): number {
  // Amounts up to ~"$9,999.99" (9 chars) keep the full size; longer ones shrink
  // proportionally so the value still fits beside the label on a phone.
  const COMFORTABLE = 9;
  if (text.length <= COMFORTABLE) return 1;
  return Math.max(0.45, COMFORTABLE / text.length);
}

interface EditableMoneyProps {
  value: number;
  onEditClick: () => void;
  display: CSSProperties;
  hintColor: string;
  ariaLabel?: string;
  disabled?: boolean;
  // How the amount is rendered. Defaults to the full `fmtMoney` form; callers
  // can pass `fmtMoneyCompact` to abbreviate very large balances (e.g. ~$2.5M).
  format?: (n: number) => string;
}

// Renders the formatted balance as a button with a pencil affordance; clicking
// it opens the edit modal (see UpdateBalanceModal). The font auto-shrinks for
// large amounts via `--gf-money-scale` so it never wraps the adjacent label.
export function EditableMoney({
  value, onEditClick, display, hintColor, ariaLabel, disabled, format = fmtMoney,
}: EditableMoneyProps) {
  const text = format(value);
  const baseFs = typeof display.fontSize === 'number' ? display.fontSize : 44;
  const scaleVar = {
    ['--gf-money-scale' as string]: String(moneyFontScale(text)),
  } as CSSProperties;
  const scaledDisplay: CSSProperties = {
    ...display,
    fontSize: `calc(${baseFs}px * var(--gf-money-scale, 1))`,
  };

  return (
    <button
      type="button"
      onClick={() => { if (!disabled) onEditClick(); }}
      disabled={disabled}
      aria-label={ariaLabel ?? `Edit balance, currently ${text}`}
      title={disabled ? undefined : 'Click to update'}
      style={{
        ...scaledDisplay,
        ...scaleVar,
        background: 'transparent',
        border: 0,
        padding: 0,
        cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span>{text}</span>
      {!disabled && <PencilIcon color={hintColor} />}
    </button>
  );
}

function PencilIcon({ color }: { color: string }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 16 16"
      fill="none" stroke="currentColor" strokeWidth="1.5"
      strokeLinecap="round" strokeLinejoin="round"
      aria-hidden style={{ flexShrink: 0, opacity: 0.7, color }}
    >
      <path d="M11.4 2.6a1.4 1.4 0 0 1 2 2L5 13 2 14l1-3 8.4-8.4Z" />
      <path d="M10 4l2 2" />
    </svg>
  );
}

